import { ALLOWED_FILE_TYPES } from 'shared/constants/validFileTypes';

async function isDicomFile(file) {
  // Slice the first 132 bytes
  const blob = file.slice(0, 132);
  const buffer = new Uint8Array(await blob.arrayBuffer());

  // Check for "DICM" magic word at offset 128
  return (
    buffer[128] === 0x44 && // 'D'
    buffer[129] === 0x49 && // 'I'
    buffer[130] === 0x43 && // 'C'
    buffer[131] === 0x4d // 'M'
  );
}

// On later versions of React Dropzone the filepath contains './' even if the file is a single file.
export const fileTypeIsValid = async (file) => {
  const ext = file.name.match(/\.([^.]+)$/)?.[1]?.toLowerCase() || '';
  if (ALLOWED_FILE_TYPES.includes(ext)) {
    return true;
  }
  if (await isDicomFile(file)) {
    return true;
  }
  return false;
};

/**
 *  Given an array of file objects, iterate and analyze the fileFullPath of each file, and
 *  return an object with nested layers according to the hierarchical folder structure;
 *
 *  @param {[object]}  items an array of file objects; each object must contain a `fileFullPath` attribute;
 */
export const getStructuredFiles = (items) => {
  let result = [];
  let level = { result };

  items.forEach((item) => {
    let path = item.fileFullPath;
    // Check if path starts with './' - this indicates a single file from React Dropzone
    // React Dropzone changed the way it handles files. Now all files have a './' prefix.
    // https://github.com/react-dropzone/react-dropzone/issues/1411
    if (path.startsWith('./')) {
      // Add directly as a file to the result
      result.push({
        ...item,
        type: 'file',
        fileFullPath: path.slice(2),
      });
    } else {
      path.split('/').reduce((r, name, i, a) => {
        if (!r[name]) {
          r[name] = { result: [] };
          const type = i === a.length - 1 ? 'file' : 'directory';
          if (type === 'file') {
            r.result.push(item);
          } else {
            if (item.Bucket && item.Key) {
              const folderKey = `${item.Key.split(name)[0] || ''}${name}`;
              r.result.push({
                name,
                type,
                Bucket: item.Bucket,
                Key: folderKey,
                children: r[name].result,
              });
            } else {
              r.result.push({
                name,
                type,
                children: r[name].result,
              });
            }
          }
        }
        return r[name];
      }, level);
    }
  });

  return result;
};

/**
 *  This is the reverse operation of `getStructuredFiles` above;
 *  Given an object with nested layers corresponding to hierarchical folder structure,
 *  recursively go through the layers, and return an array of objects. Each object in the array
 *  represents a simple file with no nested layer.
 *
 * @param {object} item an object with nested layers corresponding to hierarchical folder structure,
 *  each node must contain an attribute named `type`
 *
 */
export const flattenStructuredAttachmentsObject = (item) => {
  const res = [];
  if (item.type === 'directory') {
    item.children.forEach(function (child) {
      const tmpList = flattenStructuredAttachmentsObject(child);
      res.push(...tmpList);
    });
  } else {
    res.push({ ...item });
  }

  return res;
};

/**
 *  Given an object representing a file or a folder, return the size of it
 *   - If a file, simply return the size;
 *   - If a folder, calculate the total size for all files under it;
 *
 * @param {object} item object must contain attribute `type`; If `type` is "directory",
 *  then it also must contain attribute `size`;
 */
export const calculateFolderSize = (item) => {
  let size = 0;
  if (item.type === 'directory') {
    item.children.forEach((item) => {
      size += calculateFolderSize(item);
    });
    item.size = size;
  } else {
    size = item.size;
  }
  return size;
};

/**
 *  Given an array of objects, with each object representing a folder or a file,
 *  calculate the total size
 *
 * @param {[object]} structuredAttachments an array of objects, with each object representing a folder or a file.
 */
export const calcBatchTotalSize = (structuredAttachments) => {
  let attachments = [];
  for (var i = 0; i < structuredAttachments.length; i++) {
    const item = structuredAttachments[i];
    attachments.push(...flattenStructuredAttachmentsObject(item));
  }
  const size = attachments.reduce((accumulate, item) => {
    return accumulate + item.size;
  }, 0);
  return size;
};

export const findDuplicateAttachments = (
  attachments,
  newAttachments,
  existingAttachments
) => {
  const dups = newAttachments.filter(
    (item) =>
      attachments.some((x) => item.name === x.name) ||
      existingAttachments.some((x) => item.name === x.name)
  );

  return dups;
};

export const checkIfCanReplace = (newAttachments, existingAttachments) => {
  return !newAttachments.some((item) =>
    existingAttachments.some((x) => item.name === x.name)
  );
};
